import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, LogOut, Trophy, Calendar as CalendarIcon, Sparkles, Gift, CheckCircle2,
  Plus, RefreshCw, XCircle, Package, ShoppingBag, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { CLIENT_PORTAL_TOKEN_KEY, CLIENT_PORTAL_TENANT_KEY } from "@/pages/PortalClientLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem(CLIENT_PORTAL_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_LABEL = {
  scheduled: "Agendado",
  completed: "Concluído",
  no_show: "Não compareceu",
  cancelled: "Cancelado",
};
const STATUS_STYLE = {
  scheduled: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  completed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  no_show: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  cancelled: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};

function todayIso(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function fmtPrice(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function PortalClientHome() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [me, setMe] = useState(null);
  const [appts, setAppts] = useState([]);
  const [loyalty, setLoyalty] = useState({ items: [], rewards: [], points_balance: 0, redemption_requests: [] });
  const [packages, setPackages] = useState({ catalog: [], mine: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [redeemTarget, setRedeemTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [packageTarget, setPackageTarget] = useState(null);

  const logout = () => {
    localStorage.removeItem(CLIENT_PORTAL_TOKEN_KEY);
    localStorage.removeItem(CLIENT_PORTAL_TENANT_KEY);
    navigate(`/portal/${slug}/cliente/login`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, a, l, p] = await Promise.all([
        axios.get(`${API}/portal/client/me`, { headers: authHeaders() }),
        axios.get(`${API}/portal/client/appointments`, { headers: authHeaders() }),
        axios.get(`${API}/portal/client/loyalty`, { headers: authHeaders() }),
        axios.get(`${API}/portal/client/packages`, { headers: authHeaders() }),
      ]);
      setMe(m.data);
      setAppts(a.data.items || []);
      setLoyalty(l.data);
      setPackages(p.data);
      if (m.data?.tenant?.primary_color) {
        document.documentElement.style.setProperty("--tenant-primary", m.data.tenant.primary_color);
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        logout();
      } else {
        toast.error("Erro ao carregar.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem(CLIENT_PORTAL_TOKEN_KEY)) {
      navigate(`/portal/${slug}/cliente/login`);
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const confirmAppt = async (id) => {
    try {
      await axios.post(`${API}/portal/client/appointments/${id}/confirm`, {}, { headers: authHeaders() });
      toast.success("Presença confirmada!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar.");
    }
  };

  const submitRedeem = async () => {
    if (!redeemTarget) return;
    try {
      const { data } = await axios.post(
        `${API}/portal/client/redeem`,
        { reward_id: redeemTarget.id },
        { headers: authHeaders() },
      );
      toast.success(`Resgate solicitado! Código: ${data.request_id}`);
      setRedeemTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao resgatar.");
    }
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    try {
      await axios.post(`${API}/portal/client/appointments/${cancelTarget.id}/cancel`, {}, { headers: authHeaders() });
      toast.success("Agendamento cancelado.");
      setCancelTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar.");
    }
  };

  const submitPackageRequest = async () => {
    if (!packageTarget) return;
    try {
      const { data } = await axios.post(
        `${API}/portal/client/packages/request`,
        { package_id: packageTarget.id },
        { headers: authHeaders() },
      );
      if (data.already_pending) {
        toast.info("Você já solicitou este pacote. Aguarde a empresa confirmar.");
      } else {
        toast.success(`Pedido registrado! Código: ${data.request_id}`);
      }
      setPackageTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao solicitar pacote.");
    }
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const { client, tenant } = me;
  const upcoming = appts.filter((a) => a.status === "scheduled");
  const past = appts.filter((a) => a.status !== "scheduled");

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <header className="border-b border-border bg-background/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={tenant.name} className="h-9 w-9 rounded-md object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-tenant/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-tenant" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Portal do cliente</div>
              <div className="font-display font-semibold truncate">{tenant?.name}</div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="client-portal-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8" data-testid="client-portal-home">
        {/* Hero / Pontos */}
        <Card className="p-6 md:p-8 rounded-2xl border-border" style={{ background: "linear-gradient(135deg, hsl(var(--tenant-primary) / 0.08), transparent)" }}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Olá</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{client.name}</h1>
              {client.cpf_masked && <div className="text-sm text-muted-foreground mt-1 font-mono">{client.cpf_masked}</div>}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="flex items-center gap-1 font-display text-3xl font-bold" style={{ color: "hsl(var(--tenant-primary))" }}>
                  <Trophy className="w-6 h-6" />
                  <span data-testid="client-portal-points">{loyalty.points_balance}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">pontos</div>
              </div>
              {client.tier_name && (
                <Badge className="text-sm py-1.5 px-3 gap-1" data-testid="client-portal-tier">
                  <Sparkles className="w-3.5 h-3.5" />
                  {client.tier_name}
                </Badge>
              )}
            </div>
          </div>
        </Card>

        {/* Meus pacotes ativos + pendentes */}
        {(packages.mine?.length > 0 || packages.pending?.length > 0) && (
          <section>
            <h2 className="font-display text-xl font-semibold mb-3 flex items-center gap-2">
              <Package className="w-5 h-5" /> Meus pacotes
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {packages.mine?.map((cp) => {
                const remaining = (cp.sessions_total || 0) - (cp.sessions_used || 0);
                return (
                  <Card key={cp.id} className="p-4 rounded-xl border-border" data-testid={`my-package-${cp.id}`}>
                    <div className="font-semibold">{cp.package_name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {remaining} de {cp.sessions_total} sessões restantes
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full"
                        style={{
                          width: `${((cp.sessions_used || 0) / (cp.sessions_total || 1)) * 100}%`,
                          background: "hsl(var(--tenant-primary))",
                        }} />
                    </div>
                  </Card>
                );
              })}
              {packages.pending?.map((r) => (
                <Card key={r.id} className="p-4 rounded-xl border-dashed border-amber-500/40 bg-amber-500/5" data-testid={`pending-package-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <div className="font-semibold">{r.package_name}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Aguardando confirmação — código {r.code}
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Comprar pacotes disponíveis */}
        {packages.catalog?.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" /> Pacotes disponíveis
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {packages.catalog.map((p) => (
                <Card key={p.id} className="p-4 rounded-xl border-border flex flex-col" data-testid={`package-catalog-${p.id}`}>
                  <div className="font-semibold">{p.name}</div>
                  {p.description && <div className="text-xs text-muted-foreground mt-1">{p.description}</div>}
                  <div className="mt-2 text-xs text-muted-foreground">
                    {p.sessions}× {p.service_name || "sessões"} · validade {p.validity_days || 365} dias
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="text-sm font-mono font-semibold" style={{ color: "hsl(var(--tenant-primary))" }}>
                      {fmtPrice(p.price)}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setPackageTarget(p)}
                      data-testid={`package-request-${p.id}`}
                      style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
                    >
                      Comprar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Resgate de prêmios */}
        {loyalty.rewards && loyalty.rewards.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-semibold mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5" /> Resgatar prêmios
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {loyalty.rewards.map((r) => {
                const canRedeem = loyalty.points_balance >= (r.points_cost || 0);
                return (
                  <Card key={r.id} className="p-4 rounded-xl border-border flex flex-col"
                    data-testid={`reward-${r.id}`}>
                    <div className="font-semibold">{r.name}</div>
                    {r.description && <div className="text-xs text-muted-foreground mt-1">{r.description}</div>}
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-sm font-mono font-semibold" style={{ color: "hsl(var(--tenant-primary))" }}>
                        {r.points_cost} pts
                      </div>
                      <Button
                        size="sm"
                        disabled={!canRedeem}
                        onClick={() => setRedeemTarget(r)}
                        data-testid={`reward-redeem-${r.id}`}
                        style={canRedeem ? { background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" } : {}}
                      >
                        {canRedeem ? "Resgatar" : "Insuficiente"}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Situação dos pedidos de resgate (item 5) */}
        {Array.isArray(loyalty.redemption_requests) && loyalty.redemption_requests.length > 0 && (
          <section data-testid="client-redemption-requests">
            <h2 className="font-display text-xl font-semibold mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5" /> Meus pedidos de resgate
            </h2>
            <Card className="rounded-xl border-border">
              <ul className="divide-y divide-border">
                {loyalty.redemption_requests.map((r) => {
                  const statusLabel = r.status === "approved"
                    ? "Entregue"
                    : r.status === "rejected"
                      ? "Recusado"
                      : "Aguardando";
                  const statusTone = r.status === "approved"
                    ? "text-emerald-700 bg-emerald-500/10 border-emerald-500/40"
                    : r.status === "rejected"
                      ? "text-red-700 bg-red-500/10 border-red-500/40"
                      : "text-amber-700 bg-amber-500/10 border-amber-500/40";
                  const whenRaw = r.status === "approved"
                    ? r.approved_at
                    : r.status === "rejected"
                      ? r.rejected_at
                      : r.created_at;
                  const when = whenRaw ? new Date(whenRaw).toLocaleString("pt-BR") : "—";
                  return (
                    <li
                      key={r.id}
                      className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap"
                      data-testid={`client-redemption-request-${r.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{r.reward_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.code} · {r.points_cost} pts · {when}
                        </div>
                      </div>
                      <span className={`text-[11px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${statusTone}`}>
                        {statusLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </section>
        )}

        {/* Próximos agendamentos */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" /> Próximos agendamentos
            </h2>
            <Button size="sm" variant="outline" onClick={() => navigate(`/p/${slug}/agendar`)} data-testid="client-portal-new-appt">
              <Plus className="w-4 h-4 mr-1" /> Novo
            </Button>
          </div>
          <Card className="rounded-xl border-border">
            {upcoming.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Nenhum agendamento pendente.</div>
            ) : (
              <ul className="divide-y divide-border">
                {upcoming.map((a) => (
                  <li key={a.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`client-appt-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{a.service_name}{a.resource_name ? ` · ${a.resource_name}` : ""}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(a.start_at).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" })}
                        {a.duration_minutes ? ` · ${a.duration_minutes}min` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.confirmed_by_client ? (
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30 gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Confirmado
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => confirmAppt(a.id)}
                          data-testid={`appt-confirm-${a.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirmar
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setRescheduleTarget(a)}
                        data-testid={`appt-reschedule-${a.id}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Remarcar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setCancelTarget(a)}
                        data-testid={`appt-cancel-${a.id}`}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10">
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>

        {/* Histórico de atendimentos */}
        <section>
          <h2 className="font-display text-xl font-semibold mb-3">Histórico</h2>
          <Card className="rounded-xl border-border">
            {past.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Ainda sem atendimentos no histórico.</div>
            ) : (
              <ul className="divide-y divide-border">
                {past.slice(0, 20).map((a) => (
                  <li key={a.id} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.service_name}{a.resource_name ? ` · ${a.resource_name}` : ""}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(a.start_at).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                    <Badge variant="outline" className={STATUS_STYLE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>

        {/* Extrato de pontos */}
        <section>
          <h2 className="font-display text-xl font-semibold mb-3 flex items-center gap-2">
            <Trophy className="w-5 h-5" /> Extrato de pontos
          </h2>
          <Card className="rounded-xl border-border">
            {loyalty.items.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Ainda sem movimentações de pontos.</div>
            ) : (
              <ul className="divide-y divide-border">
                {loyalty.items.map((t) => (
                  <li key={t.id} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{t.reason || t.source || "Movimentação"}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {new Date(t.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    </div>
                    <div className={`font-mono font-semibold ${t.delta >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                      {t.delta >= 0 ? "+" : ""}{t.delta}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </section>
      </main>

      {/* Modal de confirmação de resgate */}
      <Dialog open={!!redeemTarget} onOpenChange={(o) => !o && setRedeemTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="redeem-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar resgate</DialogTitle>
            <DialogDescription>
              {redeemTarget?.name} — {redeemTarget?.points_cost} pontos
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Após resgatar, apresente o código gerado na empresa para receber sua recompensa.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRedeemTarget(null)} data-testid="redeem-cancel">Cancelar</Button>
            <Button onClick={submitRedeem} data-testid="redeem-confirm"
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              Confirmar resgate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de remarcação */}
      <Dialog open={!!rescheduleTarget} onOpenChange={(o) => !o && setRescheduleTarget(null)}>
        <DialogContent className="max-w-md" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Remarcar agendamento</DialogTitle>
            <DialogDescription>
              {rescheduleTarget?.service_name} · {rescheduleTarget && new Date(rescheduleTarget.start_at).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" })}
            </DialogDescription>
          </DialogHeader>
          {rescheduleTarget && (
            <RescheduleFlow
              appt={rescheduleTarget}
              onSuccess={() => { setRescheduleTarget(null); load(); }}
              onCancel={() => setRescheduleTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de cancelamento */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar agendamento</DialogTitle>
            <DialogDescription>
              Tem certeza que quer cancelar {cancelTarget?.service_name} — {cancelTarget && new Date(cancelTarget.start_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelTarget(null)}>Manter</Button>
            <Button variant="destructive" onClick={submitCancel} data-testid="cancel-confirm">
              <XCircle className="w-4 h-4 mr-1" /> Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de compra de pacote */}
      <Dialog open={!!packageTarget} onOpenChange={(o) => !o && setPackageTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle>Comprar pacote</DialogTitle>
            <DialogDescription>
              {packageTarget?.name} — {packageTarget && fmtPrice(packageTarget.price)}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Após confirmar, a empresa vai receber sua solicitação e liberar o pacote no próximo atendimento.
            O pagamento é feito diretamente na empresa.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPackageTarget(null)}>Cancelar</Button>
            <Button onClick={submitPackageRequest} data-testid="package-confirm"
              style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              Solicitar compra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fluxo de remarcação: escolhe data → busca slots → confirma
// ---------------------------------------------------------------------------

function RescheduleFlow({ appt, onSuccess, onCancel }) {
  const [date, setDate] = useState(todayIso(1));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [closedReason, setClosedReason] = useState("");

  const serviceId = appt.service_id;
  const resourceId = appt.resource_id || undefined;

  const fetchSlots = async (d) => {
    setLoading(true); setChosen(null); setClosedReason("");
    try {
      const params = new URLSearchParams({ service_id: serviceId, date: d });
      if (resourceId) params.set("resource_id", resourceId);
      const { data } = await axios.get(`${API}/portal/client/slots?${params.toString()}`, { headers: authHeaders() });
      if (data.closed) {
        setSlots([]);
        setClosedReason(data.reason || "Empresa fechada nesta data.");
      } else {
        setSlots(data.slots || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao buscar horários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlots(date); /* eslint-disable-next-line */ }, [date]);

  const available = useMemo(() => slots.filter((s) => s.available), [slots]);

  const submit = async () => {
    if (!chosen) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/portal/client/appointments/${appt.id}/reschedule`,
        { start_at: chosen.start_at },
        { headers: authHeaders() },
      );
      toast.success("Remarcado com sucesso!");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remarcar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nova data</Label>
        <Input type="date" value={date} min={todayIso(0)} onChange={(e) => setDate(e.target.value)}
          data-testid="reschedule-date" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Horários disponíveis</Label>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : closedReason ? (
          <div className="text-sm text-muted-foreground py-3">{closedReason}</div>
        ) : available.length === 0 ? (
          <div className="text-sm text-muted-foreground py-3">Nenhum horário livre nesse dia.</div>
        ) : (
          <div className="grid grid-cols-4 gap-2 mt-1 max-h-40 overflow-y-auto">
            {available.map((s) => (
              <button
                key={s.start_at}
                onClick={() => setChosen(s)}
                data-testid={`slot-${s.label.replace(':', '')}`}
                className={`px-2 py-1.5 rounded-md border text-xs transition-colors ${
                  chosen?.start_at === s.start_at
                    ? "text-white border-transparent"
                    : "border-border hover:bg-muted"
                }`}
                style={chosen?.start_at === s.start_at ? { background: "hsl(var(--tenant-primary))" } : {}}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Voltar</Button>
        <Button onClick={submit} disabled={!chosen || saving} data-testid="reschedule-confirm"
          style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar novo horário"}
        </Button>
      </DialogFooter>
    </div>
  );
}
